import React, { useContext, useEffect } from 'react';
import {
    Element,
    Footer as FooterBar,
} from '@99designs/apps/spa/pages/create/common/components/FooterBar';
import ProgressBar from '@99designs/apps/spa/pages/create/common/components/ProgressBar';
import Responsive from '@99designs/apps/spa/pages/create/common/components/Responsive';
import Portal from '@99designs/apps/spa/pages/create/components/Portal';
import useZendeskWebWidget from '@99designs/apps/spa/pages/create/components/zendesk/useZendeskWebWidget';
import { StepState } from '@99designs/apps/spa/pages/create/util/step';
import { Box, Button } from '@99designs/emu';
import { LayoutContext } from './Layout';

export interface Props {
    step: StepState;
    isAuthenticated: boolean;
    nextInProgress: boolean;
    nextAction: () => void;
    nextActionLabel: string | React.ReactNode;
    launchAction: () => void;
    disabled?: boolean;
}

function StepFooterActions({
    nextAction,
    nextActionLabel,
    disabled = false,
    nextInProgress,
}: Props) {
    return (
        <>
            <Responsive min="normal">
                <Box display="inline-flex">
                    <Button
                        disabled={disabled}
                        size="large"
                        width="fill"
                        onClick={nextAction}
                        loading={nextInProgress}
                    >
                        {nextActionLabel}
                    </Button>
                </Box>
            </Responsive>
            <Responsive max="medium">
                <Box display="inline-flex">
                    <Button
                        disabled={disabled}
                        width="fill"
                        onClick={nextAction}
                        loading={nextInProgress}
                    >
                        {nextActionLabel === __('Confirm designers')
                            ? __('Confirm')
                            : nextActionLabel}
                    </Button>
                </Box>
            </Responsive>
        </>
    );
}

function StepFooter(props: React.PropsWithChildren<Props>) {
    const { children, step } = props;
    const { footerRef } = useContext(LayoutContext);
    const ref = React.createRef<HTMLDivElement>();
    const zendeskWebWidget = useZendeskWebWidget();

    useEffect(() => {
        if (footerRef.current && ref.current) {
            const offset = `${ref.current.clientHeight}px`;

            // Prevent sticky footer from hiding the page footer.
            footerRef.current.style.marginBottom = offset;

            // Move Zendesk widget (loaded via GTM) above sticky footer
            zendeskWebWidget.updateSettings({ offset: { vertical: offset } });
        }

        return () => {
            if (footerRef.current) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                footerRef.current.style.marginBottom = '0px';
                zendeskWebWidget.updateSettings({ offset: { vertical: '0px' } });
            }
        };
    });

    return (
        <Portal>
            <Responsive min="normal">
                <FooterBar
                    ref={ref}
                    top={
                        <ProgressBar
                            variations={['fluid']}
                            percentageComplete={step.percentageComplete()}
                        />
                    }
                >
                    <Element align="left" cols={6}>
                        {children}
                    </Element>
                    <Element align="right" cols={6}>
                        <StepFooterActions {...props} />
                    </Element>
                </FooterBar>
            </Responsive>
            <Responsive max="medium">
                <FooterBar position="sticky" ref={ref}>
                    <Element align="left" cols={6}>
                        {children}
                    </Element>
                    <Element align="right" cols={6}>
                        <StepFooterActions {...props} />
                    </Element>
                </FooterBar>
            </Responsive>
        </Portal>
    );
}

export default StepFooter;
